import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Paper, Grid, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';

const About = () => {
    return (
        <Box>
            <Helmet>
                <title>About Play Sequence - Free Online Sequence Game | Play Sequence</title>
                <meta name="description" content="Learn about Play Sequence, a free online platform to play the classic Sequence board game with friends. Discover our mission, features, and the history behind the game." />
                <meta property="og:title" content="About Play Sequence - Free Online Sequence Game" />
                <meta property="og:description" content="Play Sequence is a free online platform bringing the classic board game to your browser. Play with friends from anywhere." />
            </Helmet>

            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#282c34', color: '#fff', py: 6, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                        About Play Sequence
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Bringing the classic board game experience to your browser
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                {/* Mission */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        Our Mission
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                        Play Sequence was created with a simple mission: to make the beloved Sequence board game accessible to everyone, regardless of physical distance. Whether you are separated from your gaming group by a few miles or by an ocean, our platform lets you enjoy the same strategic, competitive, and social experience that makes Sequence such a special game.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8 }}>
                        We believe that great games bring people together. Board games have a unique power to create shared memories, spark friendly competition, and strengthen relationships. By bringing Sequence online, we aim to preserve everything that makes the physical game wonderful while adding the convenience and accessibility of digital play. Our platform is completely free to use, with no accounts required and no downloads needed. Just share a link and start playing.
                    </Typography>
                </Paper>

                {/* History of Sequence */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        The History of Sequence
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                        The Sequence board game was invented by Doug Reuter, an inventor from Minneapolis, Minnesota. The concept for the game came to Reuter in 1974, and he spent several years refining the rules and gameplay, initially calling it "Sequence Five." He eventually secured a licensing deal with Jax Ltd., which began manufacturing and distributing the game in 1982.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                        Since its commercial release, Sequence has become one of the best-selling board games in the world, with millions of copies sold across dozens of countries. The game's appeal lies in its elegant simplicity: the rules can be learned in minutes, yet the strategic depth keeps players engaged for years. It bridges the gap between casual family games and serious strategy games, making it popular with players of all ages and skill levels.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8 }}>
                        Over the decades, several variations have been released, including Sequence for Kids, Sequence Dice, and Sequence States and Capitals. However, the original game remains the most popular version, and it is this classic experience that Play Sequence brings to the online world. Sequence is a registered trademark of Jax Ltd., and we encourage fans of the game to support the official product.
                    </Typography>
                </Paper>

                {/* Features */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        Platform Features
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    Instant Multiplayer
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    Create a game room and share the link with friends. No accounts, no downloads, no sign-ups. Everyone joins through their browser and plays in real time.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    Video and Voice Calls
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    See and talk to your friends while you play using our built-in peer-to-peer video calling feature. It is like sitting around the table together, even when you are miles apart.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    In-Game Chat
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    Use the built-in chat system to communicate with other players during the game. Send messages, react to big plays, and keep the conversation going throughout the match.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    Customizable Experience
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    Personalize your game board with custom background colors. Choose a look that suits your style or matches your team spirit for a more personalized gaming experience.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    Flexible Team Sizes
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    Play with 2 to 12 players across 2 or 3 teams. The platform handles team assignment, card dealing, and all game logic automatically so you can focus on strategy.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 2, height: '100%' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#3f51b5' }}>
                                    Automatic Rule Enforcement
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.7 }}>
                                    The game engine validates every move, tracks turn order, detects completed sequences, and announces winners. No more rule disputes or overlooked sequences.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* How It Works */}
                <Paper sx={{ p: 4, mb: 4 }}>
                    <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        How the Online Version Works
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
                        Play Sequence is built as a modern web application that runs entirely in your browser. The game state is synchronized in real time across all connected players using WebSocket technology, ensuring that every player sees the same board state simultaneously. Card shuffling and dealing are handled server-side to maintain fairness, while the client-side interface provides a smooth and responsive gaming experience.
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8 }}>
                        The video calling feature uses WebRTC, a peer-to-peer communication protocol that sends audio and video directly between players without routing through a central server. This provides low-latency communication and protects your privacy since your video and audio streams are not stored or processed by our servers.
                    </Typography>
                </Paper>

                {/* CTA */}
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        Ready to Start Playing?
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                        Create a game room, invite your friends, and experience Sequence like never before.
                    </Typography>
                    <Button component={Link} to="/" variant="contained" size="large" sx={{ backgroundColor: '#3f51b5', px: 4, '&:hover': { backgroundColor: '#303f9f' } }}>
                        Play Sequence Now
                    </Button>
                    <Box sx={{ mt: 2 }}>
                        <Button component={Link} to="/how-to-play" sx={{ color: '#3f51b5', textTransform: 'none', mr: 2 }}>
                            Learn the rules &rarr;
                        </Button>
                        <Button component={Link} to="/contact" sx={{ color: '#3f51b5', textTransform: 'none' }}>
                            Get in touch &rarr;
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default About;
